import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@elit-hair/elit-ui";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { TbAlertTriangle } from "react-icons/tb";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/utils";
import { ChatMessageRole, ChatMessage as ChatMessageType } from "@/types/chatbot.types";

type ChatMessageProps = {
    message: ChatMessageType;
    className?: string;
    onReportError?: (message: ChatMessageType) => void;
};

export function ChatMessage({ message, className, onReportError }: ChatMessageProps) {
    const { t } = useTranslation();
    const isUser = message.role === ChatMessageRole.USER;

    return (
        <div className={cn("group flex items-start gap-3", isUser && "flex-row-reverse", className)}>
            {/* Message Content */}
            <div className={cn("flex-1 min-w-0", isUser && "flex flex-col items-end")}>
                <div
                    className={cn(
                        "rounded-2xl p-2 max-w-[85%]",
                        isUser
                            ? "px-2 bg-gradient-to-r bg-gray-500/10 text-black dark:bg-gray-100/10 dark:text-white"
                            : "text-gray-900 dark:text-white p-0"
                    )}
                >
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            p: ({ ...props }) => (
                                <p
                                    className="text-sm leading-relaxed break-words select-text whitespace-pre-wrap"
                                    {...props}
                                />
                            ),
                            ul: ({ ...props }) => (
                                <ul className="list-disc pl-5 my-2 text-sm leading-relaxed" {...props} />
                            ),
                            ol: ({ ...props }) => (
                                <ol className="list-decimal pl-5 my-2 text-sm leading-relaxed" {...props} />
                            ),
                            li: ({ ...props }) => <li className="mb-1" {...props} />,
                            code: ({ className, children, ...props }) => (
                                <code
                                    className={cn(
                                        "rounded bg-black/5 dark:bg-white/10 px-1 py-0.5 text-[0.85em]",
                                        className
                                    )}
                                    {...props}
                                >
                                    {children}
                                </code>
                            ),
                            pre: ({ ...props }) => (
                                <pre
                                    className="whitespace-pre-wrap text-sm leading-relaxed font-mono bg-black/5 dark:bg-white/10 rounded-md p-3 my-2 overflow-x-auto"
                                    {...props}
                                />
                            ),
                        }}
                    >
                        {message.content}
                    </ReactMarkdown>
                </div>

                {/* Timestamp and Report Button */}
                <div className={cn("flex items-center gap-2 mt-1", isUser ? "justify-end" : "justify-start")}>
                    <div
                        className={cn(
                            "text-xs text-gray-500 dark:text-white/50 select-text",
                            isUser ? "text-right" : "text-left"
                        )}
                    >
                        {message.createdAt
                            ? DateTime.fromISO(message.createdAt).toLocaleString(DateTime.TIME_SIMPLE)
                            : ""}
                    </div>

                    {/* Report Error Button - Only for assistant messages, visible on hover */}
                    {!isUser && onReportError && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    onClick={() => onReportError(message)}
                                    className="h-12 w-12 p-0 hover:bg-orange-100 dark:hover:bg-orange-900/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                >
                                    <TbAlertTriangle className="w-10 h-10 text-orange-500" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("report_error_in_message")}</TooltipContent>
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
}
